import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Badge,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    CardHeader,
    IconButton,
    List,
    ListItem,
    Typography
} from '@mui/material';
import { keyframes } from '@mui/system';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CloseIcon from '@mui/icons-material/Close';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const pulse = keyframes`
    0% { transform: scale(1); }
    50% { transform: scale(1.08); }
    100% { transform: scale(1); }
`;

const MAX_NOTIFICATIONS = 40;

/**
 * Normalises timestamp-like inputs into comparable millisecond values.
 */
const resolveTimestamp = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    if (value instanceof Date) {
        const time = value.getTime();
        return Number.isNaN(time) ? 0 : time;
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }
    return 0;
};

/**
 * Keeps the latest notifications first while pushing sticky guidance to the end.
 */
const sortNotifications = (items) => (
    [...items].sort((a, b) => {
        if (a.sticky && !b.sticky) {
            return 1;
        }
        if (!a.sticky && b.sticky) {
            return -1;
        }
        const aTime = typeof a.timestamp === 'number' ? a.timestamp : resolveTimestamp(a.timestamp);
        const bTime = typeof b.timestamp === 'number' ? b.timestamp : resolveTimestamp(b.timestamp);
        return bTime - aTime;
    })
);

const levelToPresentation = (level) => {
    switch ((level || '').toLowerCase()) {
        case 'success':
            return { color: 'success.main', Icon: CheckCircleOutlineIcon };
        case 'warning':
            return { color: 'warning.main', Icon: WarningAmberIcon };
        case 'danger':
        case 'error':
            return { color: 'error.main', Icon: ErrorOutlineIcon };
        default:
            return { color: 'info.main', Icon: InfoOutlinedIcon };
    }
};

const formatTimestamp = (timestamp) => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
    if (Number.isNaN(date.getTime())) {
        return '';
    }
    return date.toLocaleString();
};

const truncate = (input, length = 220) => {
    if (typeof input !== 'string' || !length) {
        return input || '';
    }
    return input.length > length ? `${input.substring(0, length - 1)}...` : input;
};

const mapStatusToLevel = (status) => {
    const normalized = (status || '').toLowerCase();
    if (['processing', 'up', 'running'].includes(normalized)) {
        return 'success';
    }
    if (['stop_processing', 'finish', 'down', 'error', 'failed'].includes(normalized)) {
        return 'danger';
    }
    if (['warning', 'degraded', 'not_processing', 'paused'].includes(normalized)) {
        return 'warning';
    }
    return 'info';
};

/**
 * Builds a normalised notification payload so emitters can stay loosely coupled.
 */
const buildPayload = (entry, defaults) => {
    const resolved = resolveTimestamp(entry?.timestamp);
    const timestamp = resolved || Date.now();
    const derivedStatus = entry?.status || entry?.title || 'info';
    const derivedLevel = entry?.level || entry?.type || mapStatusToLevel(derivedStatus);

    return {
        id: entry?.id || `notification-${timestamp}-${Math.random().toString(16).slice(2)}`,
        title: entry?.status || defaults.defaultTitle,
        message: entry?.message || `${defaults.statusFallback} ${derivedStatus}`.trim(),
        status: derivedStatus,
        level: derivedLevel,
        type: entry?.type || 'info',
        timestamp,
        sticky: Boolean(entry?.sticky),
        read: Boolean(entry?.read || entry?.markRead)
    };
};

const StatusNotificationCenter = ({
    itemId,
    initialTitle,
    initialMessage,
    emptyLabel,
    updatedPrefix,
    statusFallback,
    autoSubscribe = false
}) => {
    const defaultTitle = initialTitle || 'Status Updates';
    const fallbackMessage = statusFallback || 'Status updated:';
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [panelOpen, setPanelOpen] = useState(false);
    const storageKey = useMemo(() => `item-notification:last-read:${itemId || 'default'}`, [itemId]);
    const lastReadAtRef = useRef(0);
    const apiRef = useRef(null);

    useEffect(() => {
        try {
            const stored = parseInt(window.localStorage?.getItem(storageKey) || '0', 10);
            if (!Number.isNaN(stored)) {
                lastReadAtRef.current = stored;
            }
        } catch (error) {
            console.warn('Failed to read notification center state', error);
        }
    }, [storageKey]);

    const markAllAsRead = useCallback(() => {
        const now = Date.now();
        lastReadAtRef.current = now;
        try {
            window.localStorage?.setItem(storageKey, String(now));
        } catch (error) {
            console.warn('Failed to persist notification read state', error);
        }
        setUnreadCount(0);
        setNotifications((prev) => sortNotifications(prev.map((item) => ({ ...item, read: true }))));
    }, [storageKey]);

    const pushNotification = useCallback((entry) => {
        if (!entry) {
            return;
        }
        const payload = buildPayload(entry, { defaultTitle, statusFallback: fallbackMessage });

        setNotifications((prev) => {
            const filtered = prev.filter((item) => item.id !== payload.id);
            const next = [payload, ...filtered];
            return sortNotifications(next).slice(0, MAX_NOTIFICATIONS);
        });

        const shouldTrackUnread = !payload.read && payload.timestamp > lastReadAtRef.current;
        if (shouldTrackUnread && !panelOpen) {
            setUnreadCount((prev) => Math.min(prev + 1, 999));
        }
    }, [defaultTitle, fallbackMessage, panelOpen]);

    useEffect(() => {
        const api = {
            push: pushNotification,
            markAllAsRead,
            statusFallback: fallbackMessage,
            autoSubscriberActive: false,
            supportsReact: true
        };
        apiRef.current = api;
        window.itemNotificationCenter = api;

        return () => {
            if (window.itemNotificationCenter === api) {
                delete window.itemNotificationCenter;
            }
        };
    }, [fallbackMessage, markAllAsRead, pushNotification]);

    useEffect(() => {
        if (!initialMessage) {
            return;
        }
        pushNotification({
            id: 'initial-guidance',
            title: defaultTitle,
            message: initialMessage,
            level: 'info',
            sticky: true,
            markRead: true,
            timestamp: Date.now()
        });
    }, [defaultTitle, initialMessage, pushNotification]);

    useEffect(() => {
        const handler = (event) => {
            if (!event || !event.detail) {
                return;
            }
            pushNotification(event.detail);
        };
        window.addEventListener('item-notification-center:push', handler);
        return () => {
            window.removeEventListener('item-notification-center:push', handler);
        };
    }, [pushNotification]);

    useEffect(() => {
        if (!autoSubscribe || !itemId || !window.Echo) {
            if (apiRef.current) {
                apiRef.current.autoSubscriberActive = false;
            }
            return undefined;
        }

        const channelName = `public.get-status.${itemId}`;
        const channel = window.Echo.channel(channelName);

        if (apiRef.current) {
            apiRef.current.autoSubscriberActive = true;
        }

        const listener = (payload) => {
            const statusValue = payload?.status || 'info';
            const level = mapStatusToLevel(statusValue);
            const timestamp = payload?.timestamp || Date.now();
            pushNotification({
                id: `status-${statusValue}-${timestamp}`,
                title: payload?.title || defaultTitle,
                message: payload?.message,
                status: statusValue,
                level,
                type: 'status',
                timestamp
            });

            if (statusValue === 'finish') {
                setTimeout(() => {
                    window.location.reload();
                }, 500);
            }
        };

        channel.listen('status', listener);

        return () => {
            if (window.Echo) {
                window.Echo.leaveChannel(channelName);
            }
            if (apiRef.current) {
                apiRef.current.autoSubscriberActive = false;
            }
        };
    }, [autoSubscribe, defaultTitle, itemId, pushNotification]);

    const primaryNotifications = useMemo(() => {
        const nonSticky = notifications.filter((item) => !item.sticky);
        return nonSticky.length ? nonSticky : notifications;
    }, [notifications]);

    const latestNotification = primaryNotifications[0];
    const summaryTitle = latestNotification?.title || defaultTitle;
    const summaryMessage = latestNotification
        ? truncate(latestNotification.message, 220)
        : (emptyLabel || 'No notifications yet.');
    const { Icon: SummaryIcon, color: summaryColor } = levelToPresentation(latestNotification?.level);

    const handleTogglePanel = useCallback(() => {
        setPanelOpen((prev) => {
            const next = !prev;
            if (!prev) {
                markAllAsRead();
            }
            return next;
        });
    }, [markAllAsRead]);

    const closePanel = useCallback(() => {
        setPanelOpen(false);
    }, []);

    return (
        <Box sx={{ position: 'relative' }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 2,
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    backgroundColor: (theme) => theme.palette.background.paper,
                    boxShadow: (theme) => theme.shadows[1]
                }}
            >
                <IconButton
                    color="primary"
                    aria-label="Toggle status updates"
                    onClick={handleTogglePanel}
                    sx={unreadCount > 0 ? { animation: `${pulse} 1.4s ease-in-out infinite` } : undefined}
                    size="large"
                >
                    <Badge
                        color="secondary"
                        badgeContent={Math.min(unreadCount, 99)}
                        invisible={unreadCount === 0}
                    >
                        <NotificationsActiveIcon />
                    </Badge>
                </IconButton>
                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 40, height: 40 }}
                        title={summaryTitle}
                    >
                        <SummaryIcon sx={{ fontSize: 28, color: summaryColor }} />
                    </Box>
                    <Typography variant="body2" color="text.primary" sx={{ flex: 1 }} noWrap>
                        {summaryMessage}
                    </Typography>
                </Box>
            </Box>

            {panelOpen && (
                <Card
                    variant="outlined"
                    sx={{
                        mt: 1,
                        maxWidth: 420,
                        boxShadow: (theme) => theme.shadows[8],
                        position: { sm: 'absolute' },
                        left: { sm: 0 },
                        zIndex: 1100
                    }}
                >
                    <CardHeader
                        title={summaryTitle}
                        subheader={latestNotification ? `${updatedPrefix || 'Updated'} ${formatTimestamp(latestNotification.timestamp)}` : undefined}
                        action={(
                            <IconButton aria-label="Close notifications" onClick={closePanel} size="small">
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        )}
                    />
                    <CardContent sx={{ pt: 0 }}>
                        {notifications.length === 0 ? (
                            <Typography variant="body2" color="text.secondary">
                                {emptyLabel || 'No notifications yet.'}
                            </Typography>
                        ) : (
                            <List dense sx={{ maxHeight: 280, overflowY: 'auto', pr: 1 }}>
                                {notifications.map((notification) => {
                                    const { Icon: ItemIcon, color: itemColor } = levelToPresentation(notification.level);
                                    const rawLabel = notification.status || notification.title || '';
                                    const labelText = rawLabel.replace(/_/g, ' ');
                                    const headline = notification.title || notification.status || summaryTitle;

                                    return (
                                        <ListItem
                                            key={notification.id}
                                            divider
                                            disablePadding
                                            sx={{
                                                px: 1.5,
                                                py: 1.25,
                                                alignItems: 'flex-start',
                                                bgcolor: notification.read ? 'transparent' : 'action.selected'
                                            }}
                                        >
                                            <Box sx={{ width: '100%', display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 48 }}>
                                                    <ItemIcon sx={{ fontSize: 22, color: itemColor }} />
                                                    {rawLabel ? (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{ textTransform: 'uppercase', fontWeight: 600, color: 'text.secondary', mt: 0.5 }}
                                                            noWrap
                                                        >
                                                            {labelText}
                                                        </Typography>
                                                    ) : null}
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between',
                                                            mb: 0.5
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                                            {headline}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatTimestamp(notification.timestamp)}
                                                        </Typography>
                                                    </Box>
                                                    <Typography variant="body2" color="text.primary">
                                                        {notification.message}
                                                    </Typography>
                                                    {!notification.read && (
                                                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                                            <FiberManualRecordIcon color="primary" sx={{ fontSize: 9 }} />
                                                        </Box>
                                                    )}
                                                </Box>
                                            </Box>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        )}
                    </CardContent>
                    <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                        <Button size="small" onClick={markAllAsRead} disabled={unreadCount === 0}>
                            {unreadCount === 0 ? 'All caught up' : 'Mark all as read'}
                        </Button>
                    </CardActions>
                </Card>
            )}
        </Box>
    );
};

StatusNotificationCenter.propTypes = {
    itemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    initialTitle: PropTypes.string,
    initialMessage: PropTypes.string,
    emptyLabel: PropTypes.string,
    updatedPrefix: PropTypes.string,
    statusFallback: PropTypes.string,
    autoSubscribe: PropTypes.bool
};

export default StatusNotificationCenter;
